import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { FaCar, FaSpinner, FaSearch, FaExclamationTriangle } from 'react-icons/fa';
import AutocompleteSelect from './AutocompleteSelect';
import catalogoVehiculosApiService from '@/services/catalogoVehiculosApiService';
import toastService from '@/utils/toastService';
import ValidatedInput from './ValidatedInput';

/**
 * Componente selector de vehículos con integración al catálogo global
 * Permite seleccionar marca y modelo desde la base de datos o entrada manual
 * 
 * @param {Object} props - Propiedades del componente
 * @param {string} props.marcaValue - Valor actual de la marca
 * @param {string} props.modeloValue - Valor actual del modelo  
 * @param {function} props.onMarcaChange - Callback cuando cambia la marca
 * @param {function} props.onModeloChange - Callback cuando cambia el modelo
 * @param {string} props.marcaName - Nombre del campo marca (default: 'marca_local')
 * @param {string} props.modeloName - Nombre del campo modelo (default: 'modelo_local')
 * @param {boolean} props.required - Si los campos son requeridos
 * @param {boolean} props.allowManualEntry - Permitir entrada manual (default: true)
 * @param {string} props.className - Clases CSS adicionales
 */
const VehiculoSelector = forwardRef(({
  marcaValue = '',
  modeloValue = '',
  marcaId = null,
  modeloId = null,
  onMarcaChange,
  onModeloChange,
  marcaName = 'marca_local',
  modeloName = 'modelo_local',
  required = false,
  allowManualEntry = true,
  className = ''
}, ref) => {
  //console.log('🔄 [VehiculoSelector] Props recibidos:', { marcaId, modeloId, marcaValue, modeloValue });
  const [marcas, setMarcas] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [loadingMarcas, setLoadingMarcas] = useState(false);
  const [loadingModelos, setLoadingModelos] = useState(false);
  const [catalogoEnabled, setCatalogoEnabled] = useState(true);
  const [marcaSeleccionada, setMarcaSeleccionada] = useState(null);
  const [modeloSeleccionado, setModeloSeleccionado] = useState(null);
  
  // Estado para entrada manual - comenzar en false para usar catálogo por defecto
  const [modoManual, setModoManual] = useState(false);

  // Estados de validación
  const [marcaTouched, setMarcaTouched] = useState(false);
  const [modeloTouched, setModeloTouched] = useState(false);
  const [forceShowErrors, setForceShowErrors] = useState(false);
  
  // Referencias para inputs manuales
  const marcaInputRef = useRef(null);
  const modeloInputRef = useRef(null);

  // Validación de campos
  const validateMarca = () => {
    if (!required) return '';
    const isEmpty = modoManual ? !marcaValue || marcaValue.trim() === '' : !marcaSeleccionada;
    return isEmpty ? 'Debe seleccionar marca' : '';
  };

  const validateModelo = () => {
    if (!required) return '';
    const isEmpty = modoManual ? !modeloValue || modeloValue.trim() === '' : !modeloSeleccionado;
    return isEmpty ? 'Debe seleccionar modelo' : '';
  };

  const marcaError = (marcaTouched || forceShowErrors) ? validateMarca() : '';
  const modeloError = (modeloTouched || forceShowErrors) ? validateModelo() : '';
  
  
  // Exponer métodos de validación al componente padre
  useImperativeHandle(ref, () => ({
    validateFields: () => {
      // Siempre forzar la validación, independientemente del estado anterior
      setMarcaTouched(true);
      setModeloTouched(true);
      setForceShowErrors(true);
      
      // Forzar re-render para mostrar errores
      setTimeout(() => {
        // Los errores se calculan automáticamente en el render
      }, 0);
      
      const marcaErr = validateMarca();
      const modeloErr = validateModelo();
      return !marcaErr && !modeloErr;
    },
    resetValidation: () => {
      setMarcaTouched(false);
      setModeloTouched(false);
      setForceShowErrors(false);
    }
  }));

  // Load initial data
  useEffect(() => {
    loadMarcas();
  }, []);

  // Debug useEffect para tracked prop changes
  useEffect(() => {
    //console.log('🔄 [VehiculoSelector] Props cambiaron - marcaId:', marcaId, 'modeloId:', modeloId);
  }, [marcaId, modeloId]);

  // Handle marca selection changes
  useEffect(() => {
    if (marcaSeleccionada) {
      //console.log('Cambiando marca a:', marcaSeleccionada);
      setModelos([]);
      loadModelos(marcaSeleccionada.id);
    } else {
      setModelos([]);
    }
  }, [marcaSeleccionada]);

  // Handle initial marcaId when marcas are loaded
  useEffect(() => {
    if (marcaId && marcas.length > 0) {
      const marca = marcas.find(m => m.id === parseInt(marcaId));
      if (marca) {
        setMarcaSeleccionada(marca);
        // Load modelos for this marca
        loadModelos(marca.id);
      }
    }
  }, [marcaId, marcas]);

  // Handle initial modeloId when modelos are loaded
  useEffect(() => {
    if (modeloId && modelos.length > 0) {
      //console.log('🔄 useEffect modeloId - Buscando modelo con ID:', modeloId, 'entre', modelos.length, 'modelos');
      const modelo = modelos.find(m => m.id === parseInt(modeloId));
      if (modelo) {
        //console.log('✅ useEffect - Modelo encontrado:', modelo);
        setModeloSeleccionado(modelo);
        // Update form with selected modelo
        if (onModeloChange) {
          onModeloChange({ target: { name: modeloName, value: modelo.nombre, modelo } });
        }
      } else {
        //console.log('❌ useEffect - No se encontró modelo con ID:', modeloId);
        //console.log('❌ useEffect - IDs disponibles:', modelos.map(m => m.id).slice(0, 10));
      }
    }
  }, [modeloId, modelos]);

  const loadMarcas = async () => {
    setLoadingMarcas(true);
    try {
      const response = await catalogoVehiculosApiService.getMarcas();
      
      if (response.success && response.data && response.data.length > 0) {
        // Ordenar marcas por nombre
        const marcasOrdenadas = [...response.data].sort((a, b) => a.nombre.localeCompare(b.nombre));
        setMarcas(marcasOrdenadas);
        setCatalogoEnabled(true);
        setModoManual(false);
        
        // Si hay un valor inicial de marca, buscar coincidencia
        if (marcaId) {
          //console.log('Buscando marca con ID:', marcaId);
          const marcaEncontrada = marcasOrdenadas.find(m => m.id === parseInt(marcaId));
          if (marcaEncontrada) {
            setMarcaSeleccionada(marcaEncontrada);
            // Actualizar el valor del formulario
            if (onMarcaChange) {
              onMarcaChange({ target: { name: marcaName, value: marcaEncontrada.nombre, marca: marcaEncontrada } });
            }
          }
        }
      } else {
        // Si no hay datos, no cargar nada
        console.warn('No se encontraron marcas en la base de datos');
        setMarcas([]);
        setCatalogoEnabled(false);
        setModoManual(true);
      }
    } catch (error) {
      console.error('Error cargando marcas desde la BD:', error);
      // En caso de error, no usar datos estáticos
      setMarcas([]);
      setCatalogoEnabled(false);
      setModoManual(true);
    } finally {
      setLoadingMarcas(false);
    }
  };

  const loadModelos = async (marcaId) => {
    if (!marcaId) {
      setModelos([]);
      return;
    }

    setLoadingModelos(true);
    try {
      const response = await catalogoVehiculosApiService.getModelos(marcaId);
      
      if (response.success && response.data && response.data.length > 0) {
        // Eliminar duplicados basándose en el nombre del modelo
        const modelosUnicos = response.data.filter((modelo, index, array) => 
          index === array.findIndex(m => m.nombre.toLowerCase().trim() === modelo.nombre.toLowerCase().trim())
        );
        
        // Ordenar modelos por nombre
        const modelosOrdenados = modelosUnicos.sort((a, b) => a.nombre.localeCompare(b.nombre));
        setModelos(modelosOrdenados);

        // Si hay un modeloId, seleccionarlo
        if (modeloId) {
          //console.log('🔍 Buscando modelo con ID:', modeloId, 'entre', modelosOrdenados.length, 'modelos');
          //console.log('🔍 Primeros 5 modelos disponibles:', modelosOrdenados.slice(0, 5).map(m => ({ id: m.id, nombre: m.nombre })));
          const modeloEncontrado = modelosOrdenados.find(m => m.id === parseInt(modeloId));
          if (modeloEncontrado) {
            //console.log('✅ Modelo encontrado:', modeloEncontrado);
            setModeloSeleccionado(modeloEncontrado);
            // Actualizar el valor del formulario
            if (onModeloChange) {
              onModeloChange({ 
                target: { 
                  name: modeloName, 
                  value: modeloEncontrado.nombre,
                  modelo: modeloEncontrado
                } 
              });
            }
          } else {
            //console.log('❌ No se encontró modelo con ID:', modeloId);
            //console.log('❌ IDs disponibles:', modelosOrdenados.map(m => m.id).slice(0, 10));
          }
        }
      } else {
        // Si no hay modelos, no cargar nada
        console.warn('No se encontraron modelos para la marca ID:', marcaId);
        setModelos([]);
      }
    } catch (error) {
      console.error('Error cargando modelos:', error);
      // En caso de error, no usar datos estáticos
      setModelos([]);
    } finally {
      setLoadingModelos(false);
    }
  };


  const handleMarcaSelect = (marcaId) => {
    setMarcaTouched(true);
    if (!marcaId) {
      setMarcaSeleccionada(null);
      if (onMarcaChange) {
        onMarcaChange({ target: { name: marcaName, value: '' } });
      }
      if (onModeloChange) {
        onModeloChange({ target: { name: modeloName, value: '' } });
      }
      return;
    }

    const marca = marcas.find(m => m.id === parseInt(marcaId));
    if (marca) {
      setMarcaSeleccionada(marca);
      if (onMarcaChange) {
        onMarcaChange({ target: { name: marcaName, value: marca.nombre, marca } });
      }
      // Limpiar modelo cuando cambia la marca y resetear su estado de validación
      setModeloSeleccionado(null);
      setModeloTouched(false); // Resetear el estado touched del modelo
      if (onModeloChange) {
        onModeloChange({ target: { name: modeloName, value: '' } });
      }
    }
  };

  const handleModeloSelect = (modeloId) => {
    setModeloTouched(true);
    if (!modeloId) {
      if (onModeloChange) {
        onModeloChange({ 
          target: { 
            name: modeloName, 
            value: '',
            modelo: null
          } 
        });
      }
      return;
    }

    const modelo = modelos.find(m => m.id === parseInt(modeloId));
    if (modelo && onModeloChange) {
      onModeloChange({ 
        target: { 
          name: modeloName, 
          value: modelo.nombre,
          modelo: modelo
        } 
      });
    }
  };

  const handleMarcaManualChange = (e) => {
    const value = e.target.value;
    if (onMarcaChange) {
      onMarcaChange({ target: { name: marcaName, value } });
    }
    // Limpiar modelo cuando cambia marca manual y resetear su estado de validación
    setModeloTouched(false); // Resetear el estado touched del modelo
    if (onModeloChange) {
      onModeloChange({ target: { name: modeloName, value: '' } });
    }
  };

  const handleModeloManualChange = (e) => {
    const value = e.target.value;
    if (onModeloChange) {
      onModeloChange({ target: { name: modeloName, value } });
    }
  };

  // Handlers para validación al perder foco
  const handleMarcaBlur = () => {
    setMarcaTouched(true);
  };

  const handleModeloBlur = () => {
    setModeloTouched(true);
  };

  const toggleModoManual = () => {
    setModoManual(!modoManual);
    // Limpiar selecciones al cambiar modo
    setMarcaSeleccionada(null);
    // NO resetear el estado de validación al cambiar modo
    if (onMarcaChange) {
      onMarcaChange({ target: { name: marcaName, value: '' } });
    }
    if (onModeloChange) {
      onModeloChange({ target: { name: modeloName, value: '' } });
    }
  };

  // Si el catálogo no está habilitado, solo mostrar campos manuales
  if (!catalogoEnabled) {
    return (
      <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${className}`}>
        <div>
          <label className="flex items-center text-sm font-medium mb-2">
            <FaCar className="text-gray-400 mr-2 text-xs" />
            Marca{required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <ValidatedInput
            type="text"
            name={marcaName}
            placeholder="Ej: Toyota"
            value={marcaValue}
            onChange={handleMarcaManualChange}
            className="w-full p-2 rounded-lg bg-gray-700 border border-gray-500 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
            required={required}
            requiredMessage="Debe ingresar una marca"
          />
        </div>
        
        <div>
          <label className="flex items-center text-sm font-medium mb-2">
            <FaCar className="text-gray-400 mr-2 text-xs" />
            Modelo{required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <ValidatedInput
            type="text"
            name={modeloName}
            placeholder="Ej: Corolla"
            value={modeloValue}
            onChange={handleModeloManualChange}
            className="w-full p-2 rounded-lg bg-gray-700 border border-gray-500 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
            required={required}
            requiredMessage="Debe ingresar un modelo"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Toggle entre modo catálogo y manual */}
      {allowManualEntry && (
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <FaCar className="text-blue-400 mr-2" />
            <span className="text-sm text-gray-300">
              {modoManual ? 'Entrada Manual' : 'Catálogo de Vehículos'}
            </span>
          </div>
          <button
            type="button"
            onClick={toggleModoManual}
            className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
          >
            {modoManual ? 'Usar Catálogo' : 'Entrada Manual'}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Campo Marca */}
        <div>
          <label className="flex items-center text-sm font-medium mb-2">
            <FaCar className="text-gray-400 mr-2 text-xs" />
            Marca{required && <span className="text-red-500 ml-1">*</span>}
          </label>
          
          {modoManual ? (
            <div className="relative">
              <ValidatedInput
                type="text"
                name={marcaName}
                placeholder="Ej: Toyota"
                value={marcaValue}
                onChange={handleMarcaManualChange}
                onBlur={handleMarcaBlur}
                className="w-full p-2 rounded-lg bg-gray-700 border border-gray-500 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                required={required}
                requiredMessage="Debe ingresar una marca"
              />
            </div>
          ) : (
            <div className="relative">
              <AutocompleteSelect
                options={marcas}
                value={marcaSeleccionada?.id}
                onChange={handleMarcaSelect}
                placeholder={loadingMarcas ? "Cargando marcas..." : "Seleccionar marca"}
                displayProperty="nombre"
                valueProperty="id"
                disabled={loadingMarcas}
                className={`w-full ${marcaError ? 'error-border' : ''}`}
                required={required}
                requiredMessage="Este campo es requerido"
              />
              {loadingMarcas && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <FaSpinner className="animate-spin text-gray-400" />
                </div>
              )}
              {/* Mensaje de error para marca */}
              {marcaError && (
                <p className="validation-message">
                  <FaExclamationTriangle />
                  {marcaError}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Campo Modelo */}
        <div>
          <label className="flex items-center text-sm font-medium mb-2">
            <FaCar className="text-gray-400 mr-2 text-xs" />
            Modelo{required && <span className="text-red-500 ml-1">*</span>}
          </label>
          
          {modoManual ? (
            <div className="relative">
              <ValidatedInput
                type="text"
                name={modeloName}
                placeholder="Ej: Corolla"
                value={modeloValue}
                onChange={handleModeloManualChange}
                onBlur={handleModeloBlur}
                required={required}
                requiredMessage="Debe ingresar un modelo"
              />
            </div>
          ) : (
            <div className="relative">
              <AutocompleteSelect
                options={modelos}
                value={modeloSeleccionado?.id || ''}
                onChange={handleModeloSelect}
                placeholder={
                  !marcaSeleccionada ? "Esperando marca..." :
                  loadingModelos ? "Cargando modelos..." :
                  modelos.length === 0 ? "No hay modelos disponibles" :
                  "Buscar modelo"
                }
                displayProperty="nombre"
                valueProperty="id"
                disabled={loadingModelos || !marcaSeleccionada}
                className={`w-full ${modeloError ? 'error-border' : ''}`}
                required={required}
                requiredMessage="Este campo es requerido"
              />
              {loadingModelos && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <FaSpinner className="animate-spin text-gray-400" />
                </div>
              )}
              {/* Mensaje de error para modelo */}
              {modeloError && (
                <p className="validation-message">
                  <FaExclamationTriangle />
                  {modeloError}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Información del catálogo */}
      {!modoManual && marcas.length > 0 && (
        <div className="text-xs text-gray-400 flex items-center">
          {marcaSeleccionada && modelos.length > 0 && (
            <span className="ml-2">• {modelos.length} modelos para {marcaSeleccionada.nombre}</span>
          )}
        </div>
      )}
    </div>
  );
});

export default VehiculoSelector;